// API-based storage utility functions for habit tracker
import { habitsAPI, logsAPI } from '../services/api';

// Habits
export const getHabits = async () => {
  try {
    const habits = await habitsAPI.getAll();
    return habits;
  } catch (error) {
    console.error('Error reading habits:', error);
    return [];
  }
};

export const addHabit = async (habit) => {
  try {
    const newHabit = await habitsAPI.create(habit);
    return newHabit;
  } catch (error) {
    console.error('Error adding habit:', error);
    throw error;
  }
};

export const updateHabit = async (habitId, habitData) => {
  try {
    const updatedHabit = await habitsAPI.update(habitId, habitData);
    return updatedHabit;
  } catch (error) {
    console.error('Error updating habit:', error);
    throw error;
  }
};

export const deleteHabit = async (habitId) => {
  try {
    await habitsAPI.delete(habitId);
    return true;
  } catch (error) {
    console.error('Error deleting habit:', error);
    throw error;
  }
};

// Logs
export const getLogs = async () => {
  try {
    const logs = await logsAPI.getAll();
    return logs;
  } catch (error) {
    console.error('Error reading logs:', error);
    return [];
  }
};

// Helper function to format date in local timezone (YYYY-MM-DD)
const formatDateLocal = (date) => {
  if (typeof date === 'string') return date;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const toggleHabitLog = async (habitId, date, notes = null) => {
  try {
    const dateStr = formatDateLocal(date);
    await logsAPI.toggle(habitId, dateStr, true, notes);
    return true;
  } catch (error) {
    console.error('Error toggling habit log:', error);
    throw error;
  }
};

export const isHabitCompletedOnDate = async (habitId, date, allLogs = null) => {
  try {
    const dateStr = formatDateLocal(date);
    
    // If logs are provided, use them (for optimization)
    if (allLogs) {
      return allLogs.some(log => log.habitId === habitId && log.date === dateStr);
    }
    
    // Otherwise fetch from API
    const logs = await logsAPI.getByHabit(habitId);
    return logs.some(log => log.date === dateStr);
  } catch (error) {
    console.error('Error checking habit completion:', error);
    return false;
  }
};

export const getHabitLogs = async (habitId) => {
  try {
    const logs = await logsAPI.getByHabit(habitId);
    return logs;
  } catch (error) {
    console.error('Error getting habit logs:', error);
    return [];
  }
};

// Analytics helpers
export const calculateStreak = (habitId, allLogs) => {
  const logs = allLogs.filter(log => log.habitId === habitId)
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  
  if (logs.length === 0) return 0;
  
  let streak = 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Check if completed today or yesterday (to maintain streak)
  const lastLog = new Date(logs[0].date);
  lastLog.setHours(0, 0, 0, 0);
  
  const daysDiff = Math.floor((today - lastLog) / (1000 * 60 * 60 * 24));
  
  if (daysDiff > 1) return 0; // Streak broken
  
  // Count consecutive days
  for (let i = 0; i < logs.length; i++) {
    const currentDate = new Date(logs[i].date);
    const expectedDate = new Date(today);
    expectedDate.setDate(expectedDate.getDate() - streak);
    expectedDate.setHours(0, 0, 0, 0);
    currentDate.setHours(0, 0, 0, 0);
    
    if (currentDate.getTime() === expectedDate.getTime()) {
      streak++;
    } else {
      break;
    }
  }
  
  return streak;
};

export const calculateCompletionRate = (habitId, allLogs, days = 30) => {
  const habitLogs = allLogs.filter(log => log.habitId === habitId);
  const today = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);
  
  const recentLogs = habitLogs.filter(log => {
    const logDate = new Date(log.date);
    return logDate >= startDate && logDate <= today;
  });
  
  return Math.round((recentLogs.length / days) * 100);
};

